import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ExtendedContentInsertVariable } from 'api/graphQL/types/contentInsert.types'
import { useSelectedKey } from 'components/WorkspaceEditor/NodeKeySelectionStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import {
    findRootArticle,
    getContentOwnerType,
    getParentKeyChain,
} from 'components/WorkspaceEditor/utils'
import {
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useCopyPasteShallowSelector } from '../store/CopyPasteStore'

export const useHtmlParagraphPaste = () => {
    const { projectId } = useParams<{
        projectId: string
    }>()
    const {
        contentItemViewNodes,
        selectedProjectElementNode,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        selectedProjectElementNode: state.selectedProjectElementNode,
    }))

    const selectedKey = useSelectedKey()
    const { textContentElementToAdd } = useWorkspace2Store()
    const isProductTypeElement = useMemo(() => {
        return (
            selectedProjectElementNode?.elementType ===
            ProjectElementType.PRODUCTTYPE
        )
    }, [selectedProjectElementNode])

    const productTypeNuggetId = useMemo(() => {
        return isProductTypeElement
            ? selectedProjectElementNode?.elementId ??
                  selectedProjectElementNode?.baseElementId
            : null
    }, [isProductTypeElement, selectedProjectElementNode])

    const { openPasteDialog } = useCopyPasteShallowSelector((state) => ({
        openPasteDialog: state.openPasteDialog,
    }))

    const getStartingLevel = (
        contentItemViewNodes: Partial<ContentItemViewNodesType>[],
        node: Partial<ContentItemViewNodesType>
    ): number => {
        // If we're inserting content as a sibling to the current node,
        // the starting level should match the current node's hierarchical level
        if (node.order !== undefined && node.order > 0) {
            return node.order
        }

        // Find the parent to determine the proper hierarchical level
        const parent = contentItemViewNodes.find((ci) => {
            if (node.dtvContentItemParentId) {
                return ci.id === node.dtvContentItemParentId
            }
            return ci.dtvItemId === node.dtvParentId
        })

        if (parent) {
            // If inserting as a child of the parent, start at parent's level + 1
            if (parent.order !== undefined && parent.order > 0) {
                return parent.order + 1
            }

            // Special case: if parent is an Article, children start at level 1
            if (parent.textType === 'Article') {
                return 1
            }
        }

        // Default fallback: start at level 1 for top-level content
        return 1
    }

    const getPasteIndex = (
        contentItemViewNodes: Partial<ContentItemViewNodesType>[],
        node: Partial<ContentItemViewNodesType>
    ): number => {
        // Find all siblings (same parent, partType, and textType)
        const siblings = contentItemViewNodes
            .filter(
                (ci) =>
                    ci.partType === node.partType &&
                    ci.textType === node.textType &&
                    ci.dtvParentId === node.dtvParentId
            )
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

        // Find the position of the current node among its siblings
        const currentNodeIndex = siblings.findIndex((ci) => ci.id === node.id)

        if (currentNodeIndex === -1) {
            // If current node not found among siblings, insert at the end
            return siblings.length
        }

        // When pasting, we want to insert AFTER the current node
        // So the paste index is currentNodeIndex + 1
        return currentNodeIndex + 1
    }

    const handleHtmlParagraphPaste = useCallback(
        ({
            rootArticleContentItemIds,
            ...rest
        }: ExtendedContentInsertVariable) => {
            openPasteDialog({
                input: {
                    ...rest,
                    projectId,
                    productTypeNuggetId: productTypeNuggetId,
                },
                partType: rest.partType,
                productFamilyId: rest.productFamilyId,
                rootArticleContentItemIds: rootArticleContentItemIds ?? [],
            })
        },
        [openPasteDialog, projectId, productTypeNuggetId]
    )

    const handleHtmlParagraphPasteFromInput = useCallback(
        (pastedHtml: string) => {
            const node = contentItemViewNodes.find((n) => n.id === selectedKey)
            if (!node || !node.elements) return

            const pasteIndex = getPasteIndex(contentItemViewNodes, node)
            const startingLevel = getStartingLevel(contentItemViewNodes, node)
            const rootArticleId = findRootArticle(
                node.id ?? '',
                contentItemViewNodes
            )
            const parentNode = contentItemViewNodes?.find((item) =>
                node?.dtvContentItemParentId
                    ? item?.id === node?.dtvContentItemParentId
                    : item?.dtvItemId === node?.dtvParentId
            )
            const parentKey =
                getParentKeyChain(contentItemViewNodes, node) || null
            const contentItemOwnerType = getContentOwnerType(parentNode, node)
            const contentItemOwnerId =
                contentItemOwnerType === 'ProductTypeProperty'
                    ? null
                    : node.propertyRefId || node.dtvItemId || null

            handleHtmlParagraphPaste?.({
                html: pastedHtml,
                index: pasteIndex,
                contentItemParentId:
                    node.dtvContentItemParentId || node.dtvParentId || null,
                contentItemParentType: parentNode?.type ?? 'None',
                contentItemOwnerId: contentItemOwnerId,
                contentItemOwnerType: contentItemOwnerType,
                productFamilyId: node.productFamilyId ?? '',
                productTypePropertyType: node.isSharedProductTypeProperty
                    ? 'Shared'
                    : 'Specific',
                startingLevel: startingLevel,
                partType: node.partType?.toUpperCase() ?? '',
                rootArticleContentItemIds: rootArticleId ? [rootArticleId] : [],
                parentPropertyKey: parentKey,
            })
        },
        [handleHtmlParagraphPaste, contentItemViewNodes, selectedKey]
    )

    /**
     * Handles CKEditor paste events for HTML paragraphs.
     * - If adding new content and the paste contains HTML or multiple lines, process it and prevent the default paste.
     * - Otherwise, prevent the default paste for HTML or multi-line content.
     *
     * @param evt - CKEditor paste event
     * @param data - Clipboard data from the paste event
     */
    const handlePaste = useCallback(
        (evt: any, data: any) => {
            const pastedHtml = data.dataTransfer.getData('text/html')
            const pastedText = data.dataTransfer.getData('text/plain')
            const isMultiLineContent =
                pastedText &&
                (pastedText.includes('\n') || pastedText.includes('\r'))

            const hasRichFormatting =
                pastedHtml &&
                pastedHtml.trim().length > 0 &&
                pastedText?.trim().length == 0

            // Only intercept paste for multi-line content or any content with HTML formatting
            if (isMultiLineContent || hasRichFormatting) {
                evt.stop()
                handleHtmlParagraphPasteFromInput(pastedHtml || pastedText)
                return
            }
        },
        [handleHtmlParagraphPasteFromInput]
    )

    return {
        handlePaste,
    }
}
